/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B030050 入金入力
 */
import { Box } from "@mui/material";
import { Column } from "@material-table/core";
import {
  SText,
  SCodeSelect,
  SDateField,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
  SEntryTab,
  SCancelButton,
  msgState,
  SConfirmDialogAction,
  SFileButton,
} from "components";
import {
  getSCode,
  NyukinUchiwakeKbn,
  SCode,
  TegatashubetsuCdKbn,
} from "messages/codes";
import { labels } from "messages/labels";
import { createRef, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { focusState, useFocus } from "states";
import {
  B000111Biko,
  B000111Meisaibiko,
  B000130BumonNm,
  B000140ShainNmBumonCd,
  B000150SeikyuNmr,
  catchRestError,
} from "../../clients/rest";
import { calledParam, editKbn, meishoKbn } from "../../services/codes";
import {
  B030050Create,
  B030050Delete,
  B030050DeleteRequest,
  B030050FindResponse,
  B030050Update,
} from "clients/rest/b030/B030050";
import { B000111, B000140, B000150, B030051 } from "pages";
import { useLocation, useNavigate } from "react-router-dom";
import { B000180 } from "pages/b000/B000180";
import { B000180GinkoNm } from "clients/rest/b000/B000180";
import { addDay, date, dateWithSlashAt, digit, mapIds } from "services";
import Big from "big.js";
import { getClientError, getClientMessage } from "messages";

// TODO グリッドのrenderが動かないため課題
// TODO 完了メッセージ(okだけのダイアログ)

export type B030050InputData = {
  nyukin: B030050FindResponse[];
  editKbn: string;
};

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  editKbn: string;
  nyukindenpyoNo: string;
  nyukinYmd: string;
  seikyuCd: string;
  seikyushitenCd: string;
  seikyuNmr: string;
  shainCd: string;
  shainNm: string;
  bumonCd: string;
  bumonNm: string;
  bikoCd: string;
  biko: string;
  bikoCdBefore: string;
  seikyushimeYmd: string;
  seikyushimeNo: string;
  version: string;
  nyuKinGokei: string;
}>;

// 明細入力部のフィールドの型を定義する。
type MeiFields = Partial<{
  // 新規/編集タブ
  tab: boolean;
  uchiwakeKbn: SCode;
  nyuKin: string;
  ginkoCd: string;
  ginkoshitenCd: string;
  ginkoNm: string;
  ginkoshitenNm: string;
  tegatabango: string;
  tegatashubetsuKbn: SCode;
  tegatashubetsuNm: string;
  tegatasaito: string;
  tegatafuridashiYmd: string | null;
  tempTegatafuridashiYmd: string | null;
  tegatakesaiYmd: string | null;
  tempTegatakesaiYmd: string | null;
  meisaibikoCd: string;
  meisaibiko: string;
  meisaibikoCdBefore: string;
}>;

// テーブルデータ定義
type Data = Partial<{
  gyoNo: number;
  uchiwakeKbn: string;
  uchiwakeKbnFront: SCode;
  uchiwakeKbnView: string;
  nyuKin: string;
  ginkoCd: string;
  ginkoNm: string;
  ginkoshitenNm: string;
  tegatabango: string;
  tegatashubetsuKbn: string;
  tegatashubetsuNm: string;
  tegatafuridashiYmd: string;
  tegatakesaiYmd: string;
  tegatasaito: string;
  meisaibikoCd: string;
  meisaibiko: string;
  meisaibikoCdBefore: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  editKbn: editKbn.register,
  nyukindenpyoNo: "",
  nyukinYmd: date(),
  seikyuCd: "",
  seikyushitenCd: "",
  seikyuNmr: "",
  shainCd: "",
  shainNm: "",
  bumonCd: "",
  bumonNm: "",
  bikoCd: "",
  biko: "",
  bikoCdBefore: "",
  seikyushimeYmd: "",
  seikyushimeNo: "",
  version: "",
  nyuKinGokei: "",
};

// 明細入力部の初期値を設定する。
const defaultMeiFields: MeiFields = {
  // 新規/編集タブ
  tab: true,
  uchiwakeKbn: getSCode(NyukinUchiwakeKbn, "31"),
  nyuKin: "",
  ginkoCd: "",
  ginkoshitenCd: "000",
  ginkoNm: "",
  ginkoshitenNm: "",
  tegatabango: "",
  tegatashubetsuKbn: null,
  tegatashubetsuNm: "",
  tegatasaito: "",
  tegatafuridashiYmd: null,
  tempTegatafuridashiYmd: null,
  tegatakesaiYmd: null,
  tempTegatakesaiYmd: null,
  meisaibikoCd: "",
  meisaibiko: "",
  meisaibikoCdBefore: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "3rem", align: "right" },
  { field: "uchiwakeKbn", hidden: true },
  { field: "uchiwakeKbnFront", hidden: true },
  { field: "uchiwakeKbnView", title: "内訳区分", width: "5rem" },
  {
    field: "nyuKin",
    title: "入金額　",
    width: "7rem",
    align: "right",
    render: (data) => digit(data.nyuKin),
  },
  { field: "ginkoCd", title: "銀行コード", width: "6rem" },
  { field: "ginkoNm", title: "銀行名", width: "10rem" },
  { field: "ginkoshitenNm", title: "", width: "10rem" },
  { field: "tegatabango", title: "手形番号", width: "7rem" },
  { field: "tegatashubetsuKbn", hidden: true },
  { field: "tegatashubetsuNm", title: "手形種別", width: "8rem" },
  {
    field: "tegatafuridashiYmd",
    title: "手形振出日",
    width: "8rem",
    render: (data) => {
      if (
        data.tegatafuridashiYmd! !== null &&
        data.tegatafuridashiYmd! !== ""
      ) {
        return dateWithSlashAt(data.tegatafuridashiYmd!);
      }
    },
  },
  {
    field: "tegatakesaiYmd",
    title: "手形決済日",
    width: "8rem",
    render: (data) => {
      if (data.tegatakesaiYmd! !== null && data.tegatakesaiYmd! !== "") {
        return dateWithSlashAt(data.tegatakesaiYmd!);
      }
    },
  },
  { field: "tegatasaito", title: "手形サイト", width: "5rem" },
  { field: "meisaibikoCd", hidden: true },
  { field: "meisaibiko", title: "入金明細備考", width: "20rem" },
  { field: "meisaibikoCdBefore", hidden: true },
];

const focusId = "B030050";

export const B030050 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  const meiMethods = useForm<MeiFields>({
    mode: "onBlur",
    defaultValues: defaultMeiFields,
  });
  // テーブル管理
  const tableRef = createRef();
  const [rows, setRows] = useState<Data[]>([]);
  const [rowCount, setRowCount] = useState(0);
  // 遷移元データ取得用
  const navigate = useNavigate();
  const location = useLocation();
  const inputData = location.state as B030050InputData;
  // フォーカス関数
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // テーブル行で選択されたデータ
  const [rowData, setRowData] = useState<Partial<Data>>();
  // 明細削除ボタンの活性/非活性管理(行選択の有無で判断)
  const isNotSelectDetailRow: boolean = rowData === undefined;
  // 参照モードかどうか
  const isReference = methods.watch("editKbn") === editKbn.reference;
  // 確認ダイアログのステート
  // 入金伝票番号が存在しない場合で明細が0県の時に表示するダイアログ
  const [openZeromeiConfirm, setOpenZeromeiConfirm] = useState(false);
  // 明細フィールドに入力値がある場合に表示するダイアログ
  const [openDirtymeiConfirm, setOpenDirtymeiConfirm] = useState(false);
  // 登録完了メッセージのダイアログ
  const [openCreateConfirm, setOpenCreateConfirm] = useState(false);
  // 更新完了メッセージのダイアログ
  const [openUpdateConfirm, setOpenUpdateConfirm] = useState(false);
  // 削除完了メッセージのダイアログ
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  // 内訳区分のよる活性制御
  const [isDisabledGinkoCd, setIsDisabledGinkoCd] = useState(true);
  const [isDisabledMeiFields, setIsDisabledMeiFields] = useState(true);
  // 内訳区分による必須項目制御
  const [isRequiredGinkoCd, setIsRequiredGinkoCd] = useState(false);
  const [isRequiredMeiFields, setIsRequiredMeiFields] = useState(false);

  // 初期表示イベント
  useEffect(() => {
    if (inputData === null) {
      methods.setValue("seikyushimeYmd", date());
      focus(1);
      return;
    }

    // 編集区分の設定
    methods.setValue("editKbn", inputData.editKbn);

    // 入力プロパティの反映
    // ヘッダ部
    methods.setValue("nyukindenpyoNo", inputData.nyukin[0].nyukindenpyoNo);
    methods.setValue("nyukinYmd", inputData.nyukin[0].nyukinYmd);
    methods.setValue("seikyuCd", inputData.nyukin[0].seikyuCd);
    methods.setValue("seikyushitenCd", inputData.nyukin[0].seikyushitenCd);
    methods.setValue("shainCd", inputData.nyukin[0].shainCd);
    methods.setValue("shainNm", inputData.nyukin[0].shainNm);
    methods.setValue("bumonCd", inputData.nyukin[0].bumonCd);
    methods.setValue("bumonNm", inputData.nyukin[0].bumonNm);
    methods.setValue("bikoCd", inputData.nyukin[0].bikoCd);
    methods.setValue("biko", inputData.nyukin[0].biko);
    methods.setValue("bikoCdBefore", inputData.nyukin[0].bikoCd);
    methods.setValue("seikyushimeYmd", inputData.nyukin[0].seikyushimeYmd);
    methods.setValue("seikyushimeNo", inputData.nyukin[0].seikyushimeNo);
    methods.setValue("version", inputData.nyukin[0].version);
    // 明細部
    const gridDataList = inputData.nyukin.map<Data>((value, i) => {
      // 移行データは行番号が連番ではない可能性があるため振り直しを行う
      const gyoNo: number = i + 1;

      const data: Data = {
        gyoNo,
        uchiwakeKbn: value.uchiwakeKbn,
        uchiwakeKbnFront: getSCode(NyukinUchiwakeKbn, value.uchiwakeKbn),
        uchiwakeKbnView: getSCode(NyukinUchiwakeKbn, value.uchiwakeKbn)?.label,
        nyuKin: value.nyuKin,
        ginkoCd: value.ginkoCd,
        ginkoNm: value.ginkoNm,
        ginkoshitenNm: value.shitenNm,
        tegatabango: value.tegatabango,
        tegatashubetsuKbn: value.tegatashubetsuKbn,
        tegatashubetsuNm: getSCode(TegatashubetsuCdKbn, value.tegatashubetsuKbn)
          ?.label,
        tegatafuridashiYmd: value.tegatafuridashiYmd,
        tegatakesaiYmd: value.tegatakesaiYmd,
        tegatasaito: value.tegatasaito,
        meisaibikoCd: value.meisaibikoCd,
        meisaibiko: value.meisaibiko,
        meisaibikoCdBefore: value.meisaibikoCd,
      };
      return data;
    });
    // テーブルに反映
    setRows(gridDataList);

    if (methods.watch("seikyushimeNo") && methods.watch("seikyushimeYmd")) {
      methods.setValue("editKbn", editKbn.reference);
    }

    // 初期フォーカス
    focus(1);
  }, []);

  useEffect(() => {
    handleGokeiCalculating();
    setRowCount(rows.length);
  }, [rows]);

  // コンポーネント
  // 担当者コード変更
  const handleChangeShainCd = () => {
    (async () => {
      // 担当者名表示処理
      await B000140ShainNmBumonCd(methods, navigate, setMsg);
      // 担当部門表示処理
      await B000130BumonNm(methods, navigate, setMsg);
    })();
  };

  // 内訳区分変更
  const changeUchiwakeKbn = () => {
    const meiFields = meiMethods.getValues();

    meiMethods.setValue("tempTegatafuridashiYmd", meiFields.tegatafuridashiYmd);
    meiMethods.setValue("tempTegatakesaiYmd", meiFields.tegatakesaiYmd);

    if (
      meiFields.uchiwakeKbn?.value ===
        getSCode(NyukinUchiwakeKbn, "33")?.value ||
      meiFields.uchiwakeKbn?.value === getSCode(NyukinUchiwakeKbn, "40")?.value
    ) {
      setIsDisabledGinkoCd(false);
      setIsDisabledMeiFields(false);
      setIsRequiredGinkoCd(false);
      setIsRequiredMeiFields(true);
      meiMethods.setValue(
        "tegatafuridashiYmd",
        meiFields.tempTegatafuridashiYmd
      );
      meiMethods.setValue("tegatakesaiYmd", meiFields.tempTegatakesaiYmd);
    } else if (
      meiFields.uchiwakeKbn?.value === getSCode(NyukinUchiwakeKbn, "34")?.value
    ) {
      setIsDisabledGinkoCd(false);
      setIsDisabledMeiFields(true);
      setIsRequiredGinkoCd(true);
      setIsRequiredMeiFields(false);
      meiMethods.setValue("tegatafuridashiYmd", null);
      meiMethods.setValue("tegatakesaiYmd", null);
    } else {
      setIsDisabledGinkoCd(true);
      setIsDisabledMeiFields(true);
      setIsRequiredGinkoCd(false);
      setIsRequiredMeiFields(false);
      meiMethods.setValue("tegatafuridashiYmd", null);
      meiMethods.setValue("tegatakesaiYmd", null);
    }

    // 内訳区分、入金額、名異彩備考以外空白化
    meiMethods.setValue("ginkoCd", "");
    meiMethods.setValue("ginkoNm", "");
    meiMethods.setValue("ginkoshitenNm", "");
    meiMethods.setValue("tegatabango", "");
    meiMethods.setValue("tegatashubetsuKbn", null);
    meiMethods.setValue("tegatasaito", "");
  };

  // 手形サイト、入金振出日変更
  const handleChangeSaitoHuridashiYmd = () => {
    const meiFields = meiMethods.getValues();

    // 手形サイトと入金振出日のどちらかが未入力の場合
    if (!meiFields.tegatasaito || !meiFields.tegatafuridashiYmd) {
      // 何もしない
      return;
    }

    // 手形サイトと入金振出日を加算し手形決済日にセット
    meiMethods.setValue(
      "tegatakesaiYmd",
      addDay(meiFields.tegatafuridashiYmd, meiFields.tegatasaito)
    );
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (rowData: Data) => {
    // タブを編集にする・明細登録(更新)ボタンのラベル変更
    meiMethods.reset();
    meiMethods.setValue("tab", false);
    // ダブルクリックした行を選択状態にする・明細削除ボタン活性化
    setRowData(rowData);
    // 明細入力部にデータを表示する。
    meiMethods.setValue("uchiwakeKbn", rowData.uchiwakeKbnFront!);
    meiMethods.setValue("nyuKin", rowData.nyuKin!);
    meiMethods.setValue("ginkoCd", rowData.ginkoCd!);
    meiMethods.setValue("ginkoNm", rowData.ginkoNm!);
    meiMethods.setValue("ginkoshitenNm", rowData.ginkoshitenNm!);
    meiMethods.setValue("tegatabango", rowData.tegatabango!);
    meiMethods.setValue("tegatashubetsuKbn.value", rowData.tegatashubetsuKbn!);
    meiMethods.setValue("tegatashubetsuKbn.label", rowData.tegatashubetsuNm!);
    meiMethods.setValue("tegatasaito", rowData.tegatasaito!);
    meiMethods.setValue("tegatafuridashiYmd", rowData.tegatafuridashiYmd!);
    meiMethods.setValue("tegatakesaiYmd", rowData.tegatakesaiYmd!);
    meiMethods.setValue("meisaibikoCd", rowData.meisaibikoCd);
    meiMethods.setValue("meisaibiko", rowData.meisaibiko);
    meiMethods.setValue("meisaibikoCdBefore", rowData.meisaibikoCdBefore);

    // セットされた内訳区分を読み取り活性・必須制御
    const meiFields = meiMethods.getValues();
    if (
      meiFields.uchiwakeKbn?.value ===
        getSCode(NyukinUchiwakeKbn, "33")?.value ||
      meiFields.uchiwakeKbn?.value === getSCode(NyukinUchiwakeKbn, "40")?.value
    ) {
      setIsDisabledGinkoCd(false);
      setIsDisabledMeiFields(false);
      setIsRequiredGinkoCd(false);
      setIsRequiredMeiFields(true);
    } else if (
      meiFields.uchiwakeKbn?.value === getSCode(NyukinUchiwakeKbn, "34")?.value
    ) {
      setIsDisabledGinkoCd(false);
      setIsDisabledMeiFields(true);
      setIsRequiredGinkoCd(true);
      setIsRequiredMeiFields(false);
    } else {
      setIsDisabledGinkoCd(true);
      setIsDisabledMeiFields(true);
      setIsRequiredGinkoCd(false);
      setIsRequiredMeiFields(false);
    }
  };

  // タブ切り替え
  const handleChangeMeiTab = () => {
    // 明細削除ボタン非活性化・行選択解除
    setRowData(undefined);
    // 明細入力部の値を初期化(タブも初期化、明細登録(更新)ボタンのラベルを登録にする)
    meiMethods.reset();
    changeUchiwakeKbn();
  };

  // 登録ボタン押下イベント
  const handleClickCreateButton = () => {
    const fields = methods.getValues();

    // ヘッダ部必須入力チェック
    if (!fields.nyukinYmd) {
      setMsg(getClientError("B030050_001"));
      return;
    }
    if (!fields.seikyuCd) {
      setMsg(getClientError("B030050_002"));
      return;
    }
    if (!fields.seikyushitenCd) {
      setMsg(getClientError("B030050_003"));
      return;
    }
    if (!fields.shainCd) {
      setMsg(getClientError("B030050_004"));
      return;
    }

    if (rowCount === 0) {
      if (!fields.nyukindenpyoNo) {
        setMsg(getClientError("B030050_005"));
        return;
      } else {
        setOpenZeromeiConfirm(true);
      }
    } else {
      handleOpenDirtymeiConfirm();
    }
  };

  // 登録処理
  const handleCreate = () => {
    const fields = methods.getValues();

    setRowData(undefined);
    meiMethods.reset();

    if (!fields.nyukindenpyoNo) {
      B030050Create(methods, rows)
        .then((response) => {
          methods.reset();
          meiMethods.reset();
          setRows([]);
          setMsg(getClientMessage("B030050_001", response.data.nyukindenpyoNo));
        })
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
        });
    } else {
      if (rowCount === 0) {
        const delParam: B030050DeleteRequest = {
          nyukindenpyoNo: fields.nyukindenpyoNo,
        };
        B030050Delete(delParam)
          .then((value) => {
            setMsg(getClientMessage("B030050_002"));
          })
          .catch((reason) => {
            catchRestError(reason, navigate, setMsg);
          });
      } else {
        B030050Update(methods, rows)
          .then((value) => {
            setMsg(getClientMessage("B030050_003"));
          })
          .catch((reason) => {
            catchRestError(reason, navigate, setMsg);
          });
      }
    }
  };

  // 明細登録(更新)ボタンクリックイベント()
  const handleClickCreateMeiButton = () => {
    const meiFields = meiMethods.getValues();

    // 必須入力チェック
    if (!meiFields.uchiwakeKbn) {
      setMsg(getClientError("B030050_006"));
      return;
    }
    if (!meiFields.nyuKin) {
      setMsg(getClientError("B030050_007"));
      return;
    }
    if (isRequiredGinkoCd) {
      if (!meiFields.ginkoCd) {
        setMsg(getClientError("B030050_008"));
        return;
      }
    }
    if (isRequiredMeiFields) {
      if (!meiFields.tegatabango) {
        setMsg(getClientError("B030050_009"));
        return;
      }
      if (!meiFields.tegatashubetsuKbn) {
        setMsg(getClientError("B030050_010"));
        return;
      }
      if (!meiFields.tegatafuridashiYmd) {
        setMsg(getClientError("B030050_011"));
        return;
      }
      if (!meiFields.tegatakesaiYmd) {
        setMsg(getClientError("B030050_012"));
        return;
      }
    }

    // タブを参照し登録処理もしくは更新処理を行う。
    if (meiMethods.getValues().tab === true) {
      // 登録
      handleCreateMei();
    } else {
      // 更新
      handleUpdateMei();

      // 行選択の解除
      setRowData(undefined);
    }
  };

  // 明細登録処理
  // 明細テーブルに行追加
  const handleCreateMei = () => {
    meiMethods.handleSubmit(
      () => {
        const gyoNo: number = rows.length + 1;
        const data: Data = handleDataMapping(gyoNo);

        // 明細を設定
        setRows([...rows, data]);
        setRowCount(gyoNo);

        // 明細入力部クリア
        meiMethods.reset();

        // 明細入力部の活性・必須初期化
        setIsDisabledGinkoCd(true);
        setIsDisabledMeiFields(true);
        setIsRequiredGinkoCd(false);
        setIsRequiredMeiFields(false);

        focus(7);
      },
      (errors) => {
        console.log(errors);
        setMsg("");
      }
    )();
  };

  // 明細更新処理
  // 明細テーブルの選択行を更新
  const handleUpdateMei = () => {
    // 明細に設定するデータ取得
    const gyoNo: number = rowData!.gyoNo!;
    const data: Data = handleDataMapping(gyoNo);

    // 明細を設定
    rows[gyoNo - 1] = data;
    setRows([...rows]);

    // 明細入力部クリア
    meiMethods.reset();

    // 明細入力部の活性・必須初期化
    setIsDisabledGinkoCd(true);
    setIsDisabledMeiFields(true);
    setIsRequiredGinkoCd(false);
    setIsRequiredMeiFields(false);
  };

  // 明細のデータマッピング
  const handleDataMapping = (gyoNo: number) => {
    // フォームのデータを取得する
    const meiFields = meiMethods.getValues();

    const data: Data = {
      gyoNo,
      uchiwakeKbn: meiFields.uchiwakeKbn?.value,
      uchiwakeKbnFront: meiFields.uchiwakeKbn,
      uchiwakeKbnView: meiFields.uchiwakeKbn?.label,
      nyuKin: meiFields.nyuKin,
      ginkoCd: meiFields.ginkoCd,
      ginkoNm: meiFields.ginkoNm,
      ginkoshitenNm: meiFields.ginkoshitenNm,
      tegatabango: meiFields.tegatabango,
      tegatashubetsuKbn: meiFields.tegatashubetsuKbn?.value,
      tegatashubetsuNm: meiFields.tegatashubetsuKbn?.label,
      tegatafuridashiYmd: meiFields.tegatafuridashiYmd!,
      tegatakesaiYmd: meiFields.tegatakesaiYmd!,
      tegatasaito: meiFields.tegatasaito,
      meisaibikoCd: meiFields.meisaibikoCd,
      meisaibiko: meiFields.meisaibiko,
      meisaibikoCdBefore: meiFields.meisaibikoCdBefore,
    };
    return data;
  };

  // 明細削除ボタンクリックイベント
  const handleClickDeteteMeiButton = () => {
    const gyoNo = rowData?.gyoNo;
    // 選択行の削除
    const newRows = rows.filter(function (source: Data) {
      return source.gyoNo !== gyoNo;
    });

    // 明細テーブルの行番号振り直し
    mapIds(newRows, "gyoNo");

    // 削除後のデータセット
    setRows(newRows);

    // 行選択の解除
    setRowData(undefined);

    // 明細入力部の初期化
    meiMethods.reset();

    // タブを新規にする
    meiMethods.setValue("tab", true);

    // 入金額合計表示
    handleGokeiCalculating();

    // 明細入力部の活性・必須初期化
    setIsDisabledGinkoCd(true);
    setIsDisabledMeiFields(true);
    setIsRequiredGinkoCd(false);
    setIsRequiredMeiFields(false);
  };

  // 入金合計額の算出
  const handleGokeiCalculating = () => {
    if (rows.length === 0) {
      methods.setValue("nyuKinGokei", "");
      return;
    }

    const nyuKinGokei: Big = rows.reduce((acc: Big, row: Data): Big => {
      return acc.plus(new Big(row.nyuKin ? row.nyuKin : "0"));
    }, new Big("0"));

    methods.setValue("nyuKinGokei", nyuKinGokei.toString());
  };

  // 削除確認ダイアログでキャンセル押下時
  const handleCancelZeromeiConfirm = () => {
    setOpenZeromeiConfirm(false);
  };

  // 削除確認ダイアログでOK押下時
  const handleOkZeromeiConfirm = () => {
    setOpenZeromeiConfirm(false);
    handleOpenDirtymeiConfirm();
  };

  // 明細部消去確認ダイアログ表示
  const handleOpenDirtymeiConfirm = () => {
    if (Object.keys(meiMethods.formState.touchedFields).length !== 0) {
      setOpenDirtymeiConfirm(true);
    } else {
      handleCreate();
    }
  };

  // 明細部消去確認ダイアログでキャンセル押下時
  const handleCancelDirtymeiConfirm = () => {
    setOpenDirtymeiConfirm(false);
  };

  // 明細部消去確認ダイアログでOK押下時
  const handleOkDirtymeiConfirm = () => {
    setOpenDirtymeiConfirm(false);
    handleCreate();
  };

  // 完了メッセージダイアログでOK押下時
  const handleOkCompletedConfirm = () => {
    setOpenCreateConfirm(false);
    setOpenUpdateConfirm(false);
    setOpenDeleteConfirm(false);
  };

  // レイアウト設定
  return (
    <>
      <FormProvider {...methods}>
        <SFrame
          title="入金入力"
          gridTemplateColumns="6rem 5rem 5rem 15rem 7rem 6rem 10rem 8rem 10rem"
        >
          <SFileButton
            label={labels.buttons.excel}
            align="right"
            gridColumn="9"
            dialog={<B030051 />}
          />

          <SLabel gridColumn="1">入金伝票No.</SLabel>
          <SNumberField
            name="nyukindenpyoNo"
            label="入金伝票番号"
            disabled
            gridColumn="span 2"
          />

          <SLabel required gridColumn="1">
            入金日
          </SLabel>
          <SDateField
            name="nyukinYmd"
            label="入金日"
            gridColumn="span 2"
            required
            disabled={isReference}
            {...register(1)}
          />

          <SLabel required gridColumn="1">
            請求先
          </SLabel>
          <SNumberField
            name="seikyuCd"
            label="請求先コード"
            code
            size={6}
            required
            disabled={isReference}
            onBlur={() => B000150SeikyuNmr(methods, navigate, setMsg)}
            {...register(2)}
          />
          <SNumberField
            name="seikyushitenCd"
            label="請求先支店コード"
            code
            size={4}
            disabled={isReference}
            dialog={<B000150 calledParam={calledParam.seikyu} />}
            handleSubmit={(data: any) => {
              methods.setValue("seikyuCd", data.tokuiCd);
              methods.setValue("seikyushitenCd", data.tokuishitenCd);
              methods.setValue("seikyuNmr", data.tokuiNmr);
            }}
            onBlur={() => B000150SeikyuNmr(methods, navigate, setMsg)}
            {...register(3)}
          />
          <STextField name="seikyuNmr" label="請求先名" size={24} disabled />

          <SLabel required>売掛担当者</SLabel>
          <SNumberField
            name="shainCd"
            label="売掛担当者コード"
            code
            size={7}
            required
            disabled={isReference}
            dialog={<B000140 />}
            handleSubmit={(data: any) => {
              methods.setValue("shainCd", data.shainCd);
              methods.setValue("shainNm", data.shainNm);
              methods.setValue("bumonCd", data.bumonCd);
              methods.setValue("bumonNm", data.bumonNm);
            }}
            onBlur={handleChangeShainCd}
            {...register(4)}
          />
          <STextField name="shainNm" label="売掛担当者名" size={40} disabled />

          <SLabel>売掛担当部門</SLabel>
          <STextField name="bumonNm" size={20} disabled />

          <SLabel gridColumn="1">入金備考</SLabel>
          <SNumberField
            name="bikoCd"
            label="入金備考コード"
            code="true"
            size={3}
            disabled={isReference}
            dialog={<B000111 meishoKbn={meishoKbn.biko} />}
            handleSubmit={(data: any) => {
              methods.setValue("bikoCd", data.meishoCd);
              methods.setValue("biko", data.meishoNm1);
              methods.setValue("bikoCdBefore", data?.meishoCd);
            }}
            onBlur={() => B000111Biko(methods, navigate, setMsg)}
            {...register(5)}
          />
          <STextField
            name="biko"
            label="入金備考"
            gridColumn="span 4"
            size={40}
            disabled={isReference}
            {...register(6)}
          />

          <SText gridColumn="span 12">　</SText>
          <SLabel gridColumn="span 2">入金金額合計</SLabel>
          <SText gridColumn="span 9">　</SText>
          <SNumberField
            name="nyuKinGokei"
            label="入金金額合計"
            disabled
            align="right"
            gridColumn="span 2"
          />
        </SFrame>
      </FormProvider>

      <SFrame>
        <STable
          keyName="gyoNo"
          data={rows}
          columns={columns}
          totalCount={rowCount}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
          rowSelectable={meiMethods.watch("tab")}
          width={1250}
          height={300}
        />
      </SFrame>

      {isReference ? (
        <></>
      ) : (
        <FormProvider {...meiMethods}>
          <SFrame>
            <SEntryTab
              name="tab"
              handleOk={() => {
                handleChangeMeiTab();
              }}
            ></SEntryTab>
            <SFrame gridTemplateColumns="7rem 8rem 6rem 14rem 14rem 5rem 8rem 4rem 10rem">
              <SLabel required>内訳区分</SLabel>
              <SLabel required>入金額</SLabel>
              <SLabel required={isRequiredGinkoCd}>銀行コード</SLabel>
              <SLabel gridColumn="span 2 ">銀行名</SLabel>
              <SLabel required={isRequiredMeiFields} gridColumn="span 2 ">
                手形番号
              </SLabel>
              <SLabel required={isRequiredMeiFields} gridColumn="span 2 ">
                手形種別
              </SLabel>

              <SCodeSelect
                name="uchiwakeKbn"
                label="内訳区分"
                required
                options={NyukinUchiwakeKbn}
                onChange={changeUchiwakeKbn}
                {...register(7)}
              />
              <SNumberField
                name="nyuKin"
                label="入金額"
                size={9}
                required
                align="right"
                {...register(8)}
              />
              <SNumberField
                name="ginkoCd"
                label="銀行コード"
                code
                size={4}
                required={isRequiredGinkoCd}
                disabled={isDisabledGinkoCd}
                dialog={<B000180 />}
                handleSubmit={(data: any) => {
                  meiMethods.setValue("ginkoCd", data.ginkoCd);
                  meiMethods.setValue("ginkoNm", data.ginkoNm);
                  meiMethods.setValue("ginkoshitenNm", data.shitenNm);
                }}
                onBlur={() => B000180GinkoNm(meiMethods, navigate, setMsg)}
                {...register(9)}
              />
              <STextField name="ginkoNm" label="銀行名" disabled />
              <STextField name="ginkoshitenNm" label="銀行支店名" disabled />
              <STextField
                name="tegatabango"
                label="手形番号"
                gridColumn="span 2"
                size={10}
                required={isRequiredMeiFields}
                disabled={isDisabledMeiFields}
                {...register(10)}
              />
              <SCodeSelect
                name="tegatashubetsuKbn"
                label="手形種別"
                options={TegatashubetsuCdKbn}
                empty
                gridColumn="span 2"
                required={isRequiredMeiFields}
                disabled={isDisabledMeiFields}
                {...register(11)}
              />

              <SText gridColumn="2"></SText>
              <SLabel>手形サイト</SLabel>
              <SLabel required={isRequiredMeiFields}>手形振出日</SLabel>
              <SLabel required={isRequiredMeiFields}>手形決済日</SLabel>
              <SLabel gridColumn="span 4">入金明細備考</SLabel>

              <SText gridColumn="2"></SText>
              <SNumberField
                name="tegatasaito"
                label="手形サイト"
                onBlur={handleChangeSaitoHuridashiYmd}
                size={4}
                disabled={isDisabledMeiFields}
                {...register(12)}
              />
              <SDateField
                name="tegatafuridashiYmd"
                label="手形振出日"
                onBlur={handleChangeSaitoHuridashiYmd}
                required={isRequiredMeiFields}
                disabled={isDisabledMeiFields}
                {...register(13)}
              />
              <SDateField
                name="tegatakesaiYmd"
                label="手形決済日"
                required={isRequiredMeiFields}
                disabled={isDisabledMeiFields}
                {...register(14)}
              />
              <SNumberField
                name="meisaibikoCd"
                label="入金明細備考コード"
                code
                size={3}
                dialog={<B000111 meishoKbn={meishoKbn.meisaibiko} />}
                handleSubmit={(data: any) => {
                  meiMethods.setValue("meisaibikoCd", data.meishoCd);
                  meiMethods.setValue("meisaibiko", data.meishoNm1);
                  meiMethods.setValue("meisaibikoCdBefore", data.meishoCd);
                }}
                onBlur={() => B000111Meisaibiko(meiMethods, navigate, setMsg)}
                {...register(15)}
              />
              <STextField
                name="meisaibiko"
                label="入金明細備考"
                size={30}
                gridColumn="span 3"
                {...register(16)}
              />

              {meiMethods.getValues().tab === true ? (
                <>
                  <Box gridColumn="span 8" />
                  <SNormalButton
                    label={labels.buttons.createDetail}
                    onClick={handleClickCreateMeiButton}
                    align="right"
                    {...register(17)}
                  />
                </>
              ) : (
                <>
                  <SCancelButton
                    label={labels.buttons.deleteDetail}
                    onClick={handleClickDeteteMeiButton}
                    align="left"
                    disabled={isNotSelectDetailRow}
                    {...register(18)}
                  />

                  <Box gridColumn="span 7" />

                  <SNormalButton
                    label={labels.buttons.updateDetail}
                    onClick={handleClickCreateMeiButton}
                    align="right"
                    {...register(19)}
                  />
                </>
              )}
            </SFrame>

            <SFrame gridTemplateColumns="76.5rem ">
              <SNormalButton
                label={labels.buttons.create}
                onClick={handleClickCreateButton}
                align="right"
                disabled={isReference}
                {...register(20)}
              />
            </SFrame>
          </SFrame>
          <SConfirmDialogAction
            msg={labels.dialog.nyukinDelCheck}
            onOk={handleOkZeromeiConfirm}
            onCancel={handleCancelZeromeiConfirm}
            isOpen={openZeromeiConfirm}
          />
          <SConfirmDialogAction
            msg={labels.dialog.meisaiReset}
            onOk={handleOkDirtymeiConfirm}
            onCancel={handleCancelDirtymeiConfirm}
            isOpen={openDirtymeiConfirm}
          />
          <SConfirmDialogAction
            msg={labels.dialog.createConfirmMessage}
            onOk={handleOkCompletedConfirm}
            isOpen={openCreateConfirm}
          />
          <SConfirmDialogAction
            msg={labels.dialog.updateConfirmMessage}
            onOk={handleOkCompletedConfirm}
            isOpen={openUpdateConfirm}
          />
          <SConfirmDialogAction
            msg={labels.dialog.deleteConfirmMessage}
            onOk={handleOkCompletedConfirm}
            isOpen={openDeleteConfirm}
          />
        </FormProvider>
      )}
    </>
  );
};
